import { React, useEffect, useState } from "react";
import AppWrapper from "../AppWrapper";
import { useNavigate } from "react-router-dom";
import supabase from "../../supabase-client";
import {
  Page,
  Button,
  LegacyCard,
  IndexTable,
  Text,
  Pagination,
  TextField,
  BlockStack,
  Spinner,
  Badge,
  InlineStack,
  ButtonGroup,
} from "@shopify/polaris";
import { SearchIcon } from "@shopify/polaris-icons";
import { formatFriendlyDate } from "../../utility/funcs";

function formatDuration(from, to) {
  if (!from || !to) return "";
  const ms = new Date(to) - new Date(from);
  if (isNaN(ms) || ms < 0) return "";
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) {
    const remMin = mins % 60;
    return remMin > 0 ? `${hrs}h ${remMin}m` : `${hrs}h`;
  }
  const days = Math.floor(hrs / 24);
  const remHrs = hrs % 24;
  return remHrs > 0 ? `${days}d ${remHrs}h` : `${days}d`;
}

function Validaciones() {
  const navigate = useNavigate();
  const [validaciones, setValidaciones] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [filterStatus, setFilterStatus] = useState(null);
  const [filterVisited, setFilterVisited] = useState(null);

  const pageSize = 20;

  const fetchValidaciones = async () => {
    setLoading(true);

    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    const { data, error } = await supabase.rpc("search_validaciones", {
      query_text: search?.trim() || null,
      from_index: from,
      to_index: to,
      filter_status: filterStatus,
      filter_visited: filterVisited,
    });

    if (error) {
      console.error("Error fetching validaciones:", error.message);
      setLoading(false);
      return;
    }

    setValidaciones(data);
    setHasMore(data?.length >= pageSize);
    setLoading(false);
  };

  useEffect(() => {
    fetchValidaciones();
  }, [page, filterStatus, filterVisited]);

  const statusBadge = (status) => {
    switch (status) {
      case "approved":
        return <Badge tone="success">Aprobada</Badge>;
      case "rejected":
        return <Badge tone="critical">Rechazada</Badge>;
      default:
        return <Badge tone="attention">Pendiente</Badge>;
    }
  };

  const rowMarkup = validaciones.map((v, index) => (
    <IndexTable.Row
      id={v.id}
      key={v.id}
      position={index}
      onClick={() => navigate(`/validaciones/${v.id}`)}
    >
      <IndexTable.Cell>
        <div
          style={{
            width: 200,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontWeight: 600,
          }}
          title={`${v.first_names} ${v.father_last_name} ${v.mother_last_name || ""}`}
        >
          {v.first_names} {v.father_last_name} {v.mother_last_name || ""}
        </div>
      </IndexTable.Cell>
      <IndexTable.Cell>{v.curp || "-"}</IndexTable.Cell>
      <IndexTable.Cell>{v.car_segment_text || "-"}</IndexTable.Cell>
      <IndexTable.Cell>{statusBadge(v.status)}</IndexTable.Cell>
      <IndexTable.Cell>
        {v.visited ? (
          <Badge tone="info">Visitado</Badge>
        ) : v.status === "pending" ? (
          <Badge tone="attention">Pendiente</Badge>
        ) : (
          <Badge>Pendiente</Badge>
        )}
      </IndexTable.Cell>
      <IndexTable.Cell>
        <span style={{ color: "var(--p-color-text-success)" }}>{v.approve_count}</span>
        {" / "}
        <span style={{ color: "var(--p-color-text-critical)" }}>{v.disapprove_count}</span>
      </IndexTable.Cell>
      <IndexTable.Cell>
        {formatFriendlyDate(v.created_at)}
      </IndexTable.Cell>
      <IndexTable.Cell>
        {v.decision_at ? (
          <Text tone="subdued" variant="bodySm">
            {formatDuration(v.created_at, v.decision_at)}
          </Text>
        ) : v.status === "pending" ? (
          <Text tone="subdued" variant="bodySm">
            {formatDuration(v.created_at, new Date().toISOString())} ·
            pendiente
          </Text>
        ) : (
          "-"
        )}
      </IndexTable.Cell>
    </IndexTable.Row>
  ));

  return (
    <AppWrapper>
      <Page
        title="Validaciones"
        primaryAction={
          <Button variant="primary" onClick={() => navigate("/validaciones/new")}>
            Crear Nueva
          </Button>
        }
      >
        <BlockStack gap={400}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setPage(1);
              fetchValidaciones();
            }}
          >
            <div style={{ display: "flex", gap: "0.25rem" }}>
              <span style={{ flexGrow: 1 }}>
                <TextField
                  autoComplete="off"
                  placeholder="Buscar por nombre, apellido o CURP"
                  value={search}
                  onChange={(value) => setSearch(value)}
                  disabled={loading}
                />
              </span>
              <span
                style={{
                  aspectRatio: 1,
                  display: "flex",
                  alignItems: "center",
                  width: 32,
                  height: 32,
                }}
              >
                <Button icon={SearchIcon} submit disabled={loading} />
              </span>
            </div>
          </form>
          <InlineStack gap="200">
            <ButtonGroup>
              <Button
                pressed={filterStatus === null}
                onClick={() => { setFilterStatus(null); setPage(1); }}
              >
                Todas
              </Button>
              <Button
                pressed={filterStatus === "pending"}
                onClick={() => { setFilterStatus(filterStatus === "pending" ? null : "pending"); setPage(1); }}
              >
                Pendientes
              </Button>
              <Button
                pressed={filterStatus === "approved"}
                onClick={() => { setFilterStatus(filterStatus === "approved" ? null : "approved"); setPage(1); }}
              >
                Aprobadas
              </Button>
              <Button
                pressed={filterStatus === "rejected"}
                onClick={() => { setFilterStatus(filterStatus === "rejected" ? null : "rejected"); setPage(1); }}
              >
                Rechazadas
              </Button>
            </ButtonGroup>
            <ButtonGroup>
              <Button
                pressed={filterVisited === false}
                onClick={() => { setFilterVisited(filterVisited === false ? null : false); setPage(1); }}
              >
                Visita Pendiente
              </Button>
              <Button
                pressed={filterVisited === true}
                onClick={() => { setFilterVisited(filterVisited === true ? null : true); setPage(1); }}
              >
                Visitados
              </Button>
            </ButtonGroup>
          </InlineStack>
          {loading ? (
            <Spinner size="small" />
          ) : (
            <LegacyCard>
              <>
                <IndexTable
                  resourceName={{
                    singular: "Validacion",
                    plural: "Validaciones",
                  }}
                  selectable={false}
                  itemCount={validaciones?.length ?? 0}
                  headings={[
                    { title: "Nombre" },
                    { title: "CURP" },
                    { title: "Segmento" },
                    { title: "Estado" },
                    { title: "Visita" },
                    { title: "Votos" },
                    { title: "Fecha" },
                    { title: "Tiempo" },
                  ]}
                >
                  {rowMarkup}
                </IndexTable>
                <div
                  style={{
                    padding: "1rem",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Pagination
                    hasPrevious={page > 1}
                    onPrevious={() => setPage(page - 1)}
                    hasNext={hasMore}
                    onNext={() => setPage(page + 1)}
                  />
                </div>
              </>
            </LegacyCard>
          )}
        </BlockStack>
      </Page>
    </AppWrapper>
  );
}

export default Validaciones;
